import { useLayoutEffect } from 'react'
import Image from '@/routing/Image'
import { Outlet } from 'react-router-dom'

import { AuthPreviewPanel } from '@/components/auth/AuthPreviewPanel'
import { UiLanguageSwitcher } from '@/components/auth/UiLanguageSwitcher'
import { useTheme } from '@/lib/theme-provider'
import { useTranslation } from 'react-i18next'

/**
 * Auth shell for `/login`, `/register`, etc. (pages under `src/pages/*Page/`). Uses `Outlet`.
 *
 * Resets org-specific theme overrides (preset, fonts, token overrides) so that
 * auth pages always render with Fairgen stock defaults — not a previous user's
 * white-label customization leaked from localStorage.
 * Light/dark mode preference is intentionally preserved (it's a user pref, not org branding).
 */
export function SpaAuthLayout() {
  const { t } = useTranslation('common')
  const { setPresetId, setFontFamily, setHeadingFontFamily, setThemeOverrides } = useTheme()

  useLayoutEffect(() => {
    setPresetId(null)
    setFontFamily(null)
    setHeadingFontFamily(null)
    setThemeOverrides({})
  }, [setPresetId, setFontFamily, setHeadingFontFamily, setThemeOverrides])

  return (
    <div className="fixed inset-0 flex overflow-hidden bg-background">
      <div className="absolute left-4 top-4 z-10">
        <UiLanguageSwitcher />
      </div>
      <div className="flex flex-1 items-center justify-center overflow-y-auto px-4 py-12">
        <div className="w-full max-w-[440px] animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="mb-8 flex justify-center animate-in zoom-in-95 duration-300 delay-100">
            <Image
              src="/twins-app-icon.svg"
              alt={t('logoAltTwins')}
              width={62}
              height={62}
              priority
            />
          </div>
          <div className="flex w-full flex-col gap-6">
            <Outlet />
          </div>
        </div>
      </div>

      <div className="relative hidden overflow-hidden lg:flex flex-1 bg-white dark:bg-[#060E0C]">
        <AuthPreviewPanel />
      </div>
    </div>
  )
}
